import { JobStatus } from '@/api/generatedApi';
import { Box } from '@mui/material';
import { alpha, styled, Theme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

const STATUS_LABELS: Record<JobStatus, string> = {
  [JobStatus.PENDING]: 'Pending',
  [JobStatus.RUNNING]: 'In Progress',
  [JobStatus.COMPLETED]: 'Completed',
  [JobStatus.FAILED]: 'Failed',
  [JobStatus.CANCELLED]: 'Cancelled',
};

const statusColor = (theme: Theme, status: JobStatus): string => {
  switch (status) {
    case JobStatus.PENDING: {
      return theme.palette.warning.main;
    }
    case JobStatus.RUNNING: {
      return theme.palette.info.main;
    }
    case JobStatus.COMPLETED: {
      return theme.palette.success.main;
    }
    case JobStatus.FAILED: {
      return theme.palette.error.main;
    }
    case JobStatus.CANCELLED: {
      return theme.palette.text.secondary;
    }
  }
};

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const Badge = styled(Box, { shouldForwardProp: property => property !== 'status' })<{ status: JobStatus }>(({
  theme,
  status,
}) => {
  const color = statusColor(theme, status);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '22px',
    padding: '0 10px',
    borderRadius: '11px',
    backgroundColor: alpha(color, 0.14),
    color,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  };
});

const StatusDot = styled('span', { shouldForwardProp: property => property !== 'status' })<{ status: JobStatus }>(
  ({ theme, status }) => ({
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: statusColor(theme, status),
    ...(status === JobStatus.RUNNING && {
      animation: `${pulse} 1.4s ease-in-out infinite`,
    }),
  })
);

export const StatusBadge = ({
  status,
  progress,
  'data-testid': testId,
}: {
  status: JobStatus;
  progress?: number;
  'data-testid'?: string;
}) => {
  const showProgress = status === JobStatus.RUNNING && progress !== undefined && progress > 0;
  const label = showProgress ? `${STATUS_LABELS[status]} ${Math.round(progress)}%` : STATUS_LABELS[status];

  return (
    <Badge status={status} data-testid={testId}>
      <StatusDot status={status} />
      {label}
    </Badge>
  );
};
